import { Trans, useLingui } from "@lingui/react/macro";
import type { MiniAppSnapshot } from "@rakazo/contracts";
import { Button, Spinner } from "@rakazo/ui-web";
import { Download } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MiniAppSaveQueue } from "../lib/mini-app-save";
import { downloadArtifactBytes } from "../lib/artifact-open";
import { useQueryUserId } from "../lib/query";
import { createSpaceRpc, selectedSpaceId } from "../lib/rpc";
import { useBoundSpaceId } from "../lib/space-rpc";
import { SandboxedHtmlViewer } from "./SandboxedHtmlViewer";

export function MiniAppViewer(props: {
  html: string;
  title: string;
  artifactId: string;
  spaceId?: string | null;
}) {
  const boundSpace = useBoundSpaceId();
  const userId = useQueryUserId();
  const spaceId = props.spaceId === undefined ? (boundSpace ?? selectedSpaceId()) : props.spaceId;
  return (
    <BoundMiniAppViewer
      key={`${userId}:${spaceId}:${props.artifactId}`}
      {...props}
      spaceId={spaceId}
    />
  );
}

function BoundMiniAppViewer({
  html,
  title,
  artifactId,
  spaceId,
}: {
  html: string;
  title: string;
  artifactId: string;
  spaceId: string | null;
}) {
  const { t } = useLingui();
  const [state, setState] = useState<{
    initial?: MiniAppSnapshot;
    status: "loading" | "saved" | "saving" | "error";
    conflict?: boolean;
  }>({ status: "loading" });
  const queue = useRef<MiniAppSaveQueue | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  useEffect(() => {
    let mounted = true;
    const client = createSpaceRpc(spaceId);
    void client.artifacts
      .appData({ artifactId })
      .then((initial) => {
        if (!mounted) return;
        queue.current = new MiniAppSaveQueue(
          initial,
          (values, revision, mutationId) =>
            client.artifacts.saveAppData({ artifactId, values, revision, mutationId }),
          (status, error) => {
            if (mounted)
              setState((old) => ({
                ...old,
                status,
                conflict:
                  !!error &&
                  typeof error === "object" &&
                  "code" in error &&
                  error.code === "CONFLICT",
              }));
          },
        );
        setState({ initial, status: "saved" });
      })
      .catch(() => {
        if (mounted) setState({ status: "error" });
      });
    return () => {
      mounted = false;
    };
  }, [artifactId, spaceId, loadAttempt]);
  useEffect(() => {
    if (state.status !== "saving" && state.status !== "error") return;
    const leave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", leave);
    return () => window.removeEventListener("beforeunload", leave);
  }, [state.status]);
  if (!state.initial)
    return (
      <div className="grid h-full place-items-center p-4">
        {state.status === "loading" ? (
          <Spinner aria-label={t`Loading app data…`} />
        ) : (
          <div role="alert" className="space-y-3 text-destructive">
            <p>
              <Trans>Could not load app data. Reconnect and try again.</Trans>
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setState({ status: "loading" });
                setLoadAttempt((n) => n + 1);
              }}
            >
              <Trans>Retry</Trans>
            </Button>
          </div>
        )}
      </div>
    );
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="mini-app-viewer">
      <div
        className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-b border-border bg-background px-3 py-1.5 text-base"
        role="status"
        aria-live="polite"
      >
        <span className={state.status === "error" ? "text-destructive" : "text-muted-foreground"}>
          {state.status === "saving" ? (
            <Trans>Saving…</Trans>
          ) : state.status === "saved" ? (
            <Trans>Saved</Trans>
          ) : state.conflict ? (
            <Trans>Changed in another window. Download your data, then reopen this app.</Trans>
          ) : (
            <Trans>Changes aren’t saved. Retry or download your data.</Trans>
          )}
        </span>
        {state.status === "error" && !state.conflict ? (
          <Button variant="outline" size="sm" onClick={() => queue.current?.retry()}>
            <Trans>Retry</Trans>
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          aria-label={t`Download app data`}
          onClick={() =>
            downloadArtifactBytes(
              `${title}-data.json`,
              "application/json",
              new TextEncoder().encode(JSON.stringify(queue.current?.latest ?? {}, null, 2)),
            )
          }
        >
          <Download size={16} />
          <Trans>App data</Trans>
        </Button>
      </div>
      <div className="min-h-0 flex-1">
        <SandboxedHtmlViewer
          html={html}
          title={title}
          storage={{
            initial: state.initial.values,
            save: (values) => queue.current!.enqueue(values),
          }}
        />
      </div>
    </div>
  );
}
